import React, { createRef, useCallback, useEffect, useState } from "react";

import { copy } from "shared/assets";
import { IAPIState, IStatus, IPager } from "shared/types";
import { DeleteEquipmentModal, EditEquipmentModal } from "Venues/containers";
import { Dimensions, Pager, Table, TableColumn, TableSkeleton, Weight } from "shared/components";
import { Spacer } from "shared/layout";
import { IEquipment } from "Venues/types";
import { getEquipmentMainCategory, getEquipmentSubCategory } from "Venues/utils";
import { EquipmentDetail } from "./components";
import { formatNumber } from "shared/utils";

const { equipmentProperties, createEquipment, actions } = copy;

export enum EquipmentTableType {
  Category,
  MoreInfo,
}

interface EquipmentTableProps {
  equipment: IEquipment[];
  apiStatus?: IAPIState;
  showEmpty?: boolean;
  pager?: IPager;
  fetchEquipment: (page?: number) => void;
  type?: EquipmentTableType;
  showEditActions?: boolean;
  nbSkeletonRows?: number;
}

export const EquipmentTable: React.FC<EquipmentTableProps> = ({
  equipment = [],
  apiStatus,
  showEmpty = true,
  pager,
  fetchEquipment,
  type = EquipmentTableType.Category,
  showEditActions = true,
  nbSkeletonRows = 3,
}) => {
  const scrollRef = createRef<HTMLDivElement>();
  const [selectedEquipment, setSelectedEquipment] = useState<IEquipment>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openRow, setOpenRow] = useState<{ id: string; content: JSX.Element } | undefined>();
  const [oldEquipmentList, setOldEquipmentList] = useState<IEquipment[]>(equipment);

  // update open row when equipment is edited or deleted
  useEffect(() => {
    if (equipment !== oldEquipmentList && openRow) {
      const updatedEquipment = equipment.find(({ id }) => id === openRow.id);

      if (updatedEquipment) {
        setOpenRow({
          id: openRow.id,
          content: <EquipmentDetail {...updatedEquipment} />,
        });
      } else {
        setOpenRow(undefined);
      }
      setOldEquipmentList(equipment);
    }
  }, [equipment, oldEquipmentList, openRow]);

  const getRowActions = useCallback(
    (id: string) => {
      const getEquipmentFromId = (id: string): IEquipment | undefined => {
        return equipment.find(item => item.id === id);
      };

      const infoAction = {
        label: actions.info,
        handleClick: () => {
          // if current row is open, close the row
          if (openRow?.id === id) {
            setOpenRow(undefined);
            return;
          }

          const equipment = getEquipmentFromId(id);

          if (equipment) {
            setOpenRow({ id, content: <EquipmentDetail {...equipment} /> });
          }
        },
      };

      const editActions = [
        {
          label: actions.edit,
          dataTestId: "edit",
          handleClick: () => {
            setSelectedEquipment(getEquipmentFromId(id));
            setShowEditModal(true);
          },
        },
        {
          label: actions.delete,
          dataTestId: "delete",
          handleClick: () => {
            setSelectedEquipment(getEquipmentFromId(id));
            setShowDeleteModal(true);
          },
          status: IStatus.error,
        },
      ];

      const actionsArray = [infoAction];

      if (showEditActions) {
        return actionsArray.concat(editActions);
      }

      return actionsArray;
    },
    [equipment, showEditActions, openRow]
  );

  const handlePageChange = (page: number) => {
    // scroll to top of table
    scrollRef.current?.scrollIntoView();

    pager?.onPageChange(page);
  };

  const handleDeleteOnSuccess = () => {
    const { currentPage = 1 } = pager || {};
    if (currentPage !== 1 && equipment.length === 1) {
      fetchEquipment(currentPage - 1);
    } else {
      fetchEquipment(currentPage);
    }
  };

  const renderTable = () => {
    if (apiStatus === IAPIState.pending)
      return <TableSkeleton nbRows={equipment.length ? equipment.length : nbSkeletonRows} />;

    return (
      <Table
        data={equipment}
        columns={type === EquipmentTableType.Category ? categoryColumns : moreInfoColmuns}
        emptyCopy={createEquipment.table.empty}
        getRowActions={getRowActions}
        openRow={openRow}
        closeOpenRow={() => setOpenRow(undefined)}
      />
    );
  };

  if (!showEmpty && equipment.length === 0 && apiStatus !== IAPIState.pending) return null;

  return (
    <>
      {showEditModal && (
        <EditEquipmentModal
          equipment={selectedEquipment}
          handleClose={() => setShowEditModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteEquipmentModal
          equipment={selectedEquipment}
          handleClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteOnSuccess}
        />
      )}
      <Spacer size="s">
        <div ref={scrollRef} data-scroll-ref />
        {renderTable()}
        {pager && <Pager {...pager} onPageChange={handlePageChange} />}
      </Spacer>
    </>
  );
};

const categoryColumns: TableColumn[] = [
  {
    Header: equipmentProperties.units.title,
    accessor: "amount",
    Cell: ({ value }) => formatNumber(value),
  },
  {
    Header: equipmentProperties.details.title,
    accessor: "brand_model",
    Cell: ({ row }) => {
      const { brand, model } = row.original as IEquipment;
      return `${brand} ${model}`;
    },
  },
  {
    Header: equipmentProperties.category.title,
    accessor: "main_category",
    Cell: ({ row }) => {
      const { category } = row.original as IEquipment;
      return getEquipmentMainCategory(category)?.label || "";
    },
  },
  {
    Header: equipmentProperties.subcategory.title,
    accessor: "category",
    Cell: ({ value }) => getEquipmentSubCategory(value)?.label || "",
  },
];

const moreInfoColmuns: TableColumn[] = [
  {
    Header: equipmentProperties.units.title,
    accessor: "amount",
  },
  {
    Header: equipmentProperties.details.title,
    accessor: "brand_model",
    Cell: ({ row }) => {
      const { brand, model } = row.original as IEquipment;
      return `${brand} ${model}`;
    },
  },
  {
    Header: equipmentProperties.dimensions.title,
    accessor: "dimensions",
    Cell: ({ row }) => {
      const { dimensions } = row.original as IEquipment;
      return dimensions && <Dimensions dimensions={dimensions} />;
    },
  },
  {
    Header: equipmentProperties.weight.title,
    accessor: "weight",
    Cell: ({ row }) => {
      const { weight } = row.original as IEquipment;
      return weight && <Weight weight={weight} />;
    },
  },
];
